import usePost from '../../../core/composables/api/usePost'
import { useLocalFeedbacks } from '../../../core/composables/useLocalFeedbacks'
import { ApiRequestStatus } from '../../../core/models/api/api-request-status'
import { useSnackStore } from '../../../stores/snack.store'
import type CreatableFeedback from '../models/creatable-feedback'
import { useI18n } from 'vue-i18n'
import useGet from '../../../core/composables/api/useGet'
import type { QuestionGroup } from '../models/question-group'
import { nextTick, onMounted, provide } from 'vue'
import { useEventBus, useLocalStorage } from '@vueuse/core'
import type { Question } from '@/core/models/question'
import { giveFeedbackKey } from './useGiveStructuredFeedback'
import { useWysiwygPlainText } from '@/core/composables/use-wysiwyg-plain-text'

export const usePublicGiveStructuredFeedback = (token: string) => {
  const { t } = useI18n()
  const snackStore = useSnackStore()
  const { hasTextContent } = useWysiwygPlainText()

  const {
    data,
    status: loadingState,
    load,
  } = useGet<GiveFeedbackPage>('token/' + token + '/feedback', { omitOrganization: true })

  onMounted(async () => {
    await load()
  })

  const state = useLocalStorage<'start' | 'feedback' | 'finished' | 'saved'>(
    'public-state-' + token,
    'start',
  )

  const skipStartScreen = () => {
    state.value = 'feedback'
  }

  const bus = useEventBus<{
    feedback: Omit<CreatableFeedback, 'feedbackReceiverId'>
    question: Question
    questionGroup: QuestionGroup
  }>('feedbacks-' + token)

  const onFeedbackUpdated = bus.on

  const updateFeedback = (newFeedback: Omit<CreatableFeedback, 'feedbackReceiverId'>) => {
    const existingFeedback = feedbacks.value.find(
      (feedback) =>
        feedback.questionId === newFeedback.questionId &&
        feedback.questionGroupId === newFeedback.questionGroupId,
    )

    const creatableFeedback = {
      ...existingFeedback,
      ...newFeedback,
    }

    feedbacks.value = [
      ...feedbacks.value.filter(
        (feedback) =>
          feedback.questionId != newFeedback.questionId ||
          feedback.questionGroupId != newFeedback.questionGroupId,
      ),
    ]

    if (
      hasTextContent(creatableFeedback.text) ||
      creatableFeedback.value !== undefined ||
      (creatableFeedback.subFeedbacks?.length ?? 0) > 0
    ) {
      feedbacks.value.push(creatableFeedback)
    }

    const questionGroup = data.value?.questionGroups.find(
      (questionGroup) => questionGroup.id == newFeedback.questionGroupId,
    )

    const question = questionGroup?.questions.find(
      (question) => question.id == newFeedback.questionId,
    )

    if (questionGroup && question) {
      bus.emit({ feedback: creatableFeedback, question: question, questionGroup: questionGroup })
    }
  }

  const { mutate: saveFeedbacks, status: saveFeedbackStatus } = usePost<
    string,
    {
      feedbacks: Omit<CreatableFeedback, 'feedbackReceiverId'>[]
    }
  >('token/' + token + '/feedback', true)

  const { feedbacksByPublicToken } = useLocalFeedbacks()
  const feedbacks = feedbacksByPublicToken(token)

  const save = async () => {
    await saveFeedbacks({
      feedbacks: feedbacks.value,
    })

    switch (saveFeedbackStatus.value) {
      case ApiRequestStatus.Success:
        state.value = 'saved'
        break
      case ApiRequestStatus.Error:
        snackStore.showError(t('feedback.giveFeedbackError'))
        break
    }
  }

  const navigateToQuestionByIdBus = useEventBus<{
    question: { questionId: string; questionGroupId: string }
  }>('navigateToQuestionById-' + token)

  const navigateToQuestionById = (question: { questionId: string; questionGroupId: string }) => {
    // SingleQuestionsView is unmounted while state==='finished' (v-if), so emit after it remounts
    state.value = 'feedback'
    nextTick(() => {
      navigateToQuestionByIdBus.emit({ question })
    })
  }

  const onNavigateToQuestionById = navigateToQuestionByIdBus.on

  provide(giveFeedbackKey, {
    onFeedbackUpdated,
    save,
    updateFeedback,
    feedbacks,
    state,
    navigateToQuestionById,
    onNavigateToQuestionById,
  })

  return { data, loadingState, state, skipStartScreen }
}

interface GiveFeedbackPage {
  readonly id: string
  readonly title?: string
  readonly message?: JSON
  readonly receiverName: string
  readonly organizationName: string
  readonly timeEstimate: number
  readonly questionGroups: ReadonlyArray<QuestionGroup>
  readonly perspective: {
    readonly key: string
    readonly nameLong: string
    readonly nameShort: string
    readonly icon: string
  }
}
